/**
 * design.md §P8 — Preview. A layout plan can be shown on the live screens behind it
 * before anyone approves it. Nothing is saved: the provider holds the plan's view
 * configs in memory, every screen reads its views through `usePreviewedViews`, and
 * a bar says "Previewing unsaved changes" with Apply and Discard until one is chosen.
 */

import { useMutation } from '@tanstack/react-query'
import { Check, Eye, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { api, ApiError } from '@/lib/api'
import type { SchemaChange, ViewConfig, ViewDef, ViewKind } from '@/lib/meta'

interface PreviewView {
  table?: string
  app?: string
  slug: string
  kind?: ViewKind
  name?: string
  config: ViewConfig
}

interface PreviewState {
  plan: SchemaChange
  views: PreviewView[]
}

interface PreviewContextValue {
  preview: PreviewState | null
  start: (plan: SchemaChange) => void
  stop: () => void
}

const PreviewContext = React.createContext<PreviewContextValue>({
  preview: null,
  start: () => undefined,
  stop: () => undefined,
})

/** The view changes a plan carries, in a form screens can merge. */
function viewsOf(plan: SchemaChange): PreviewView[] {
  return plan.ops
    .filter((op) => op.op === 'update_view' || op.op === 'create_view')
    .map((op) => ({
      table: op.table as string | undefined,
      app: op.app as string | undefined,
      slug: String(op.op === 'update_view' ? (op.view ?? 'all') : op.slug),
      kind: op.kind as ViewKind | undefined,
      name: op.name as string | undefined,
      config: (op.config ?? {}) as ViewConfig,
    }))
}

export function canPreview(plan: SchemaChange): boolean {
  return plan.layout_only && plan.status === 'planned' && viewsOf(plan).length > 0
}

export function PreviewProvider({ children }: { children: React.ReactNode }) {
  const [preview, setPreview] = React.useState<PreviewState | null>(null)
  const value = React.useMemo(
    () => ({
      preview,
      start: (plan: SchemaChange) => setPreview({ plan, views: viewsOf(plan) }),
      stop: () => setPreview(null),
    }),
    [preview],
  )
  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
}

export function usePreview() {
  return React.useContext(PreviewContext)
}

/**
 * The views a screen should render: the saved ones, with any previewed change laid
 * over them and any previewed new view added. `owner` is the table key, or for app
 * pages the app slug.
 */
export function usePreviewedViews(
  owner: { table?: string; app?: string },
  views: ViewDef[],
): ViewDef[] {
  const { preview } = usePreview()
  return React.useMemo(() => {
    if (!preview) return views
    const mine = preview.views.filter((v) =>
      owner.table ? v.table === owner.table : v.app === owner.app,
    )
    if (mine.length === 0) return views
    const merged = views.map((view) => {
      const change = mine.find((v) => v.slug === view.slug && !view.personal)
      return change
        ? { ...view, name: change.name ?? view.name, config: change.config }
        : view
    })
    for (const change of mine) {
      if (!merged.some((v) => v.slug === change.slug)) {
        merged.push({
          id: `preview-${change.slug}`,
          slug: change.slug,
          name: change.name ?? change.slug,
          kind: change.kind ?? (owner.app ? 'dashboard' : 'list'),
          config: change.config,
          is_default: false,
        })
      }
    }
    return merged
  }, [preview, views, owner.table, owner.app])
}

/** The persistent bar design.md §P8 requires while a preview is showing. */
export function PreviewBar() {
  const { preview, stop } = usePreview()
  const [error, setError] = React.useState<string | null>(null)

  const apply = useMutation({
    mutationFn: () => api.post<SchemaChange>(`/api/meta/changes/${preview?.plan.id}/apply`),
    onSuccess: () => {
      setError(null)
      stop()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not apply.'),
  })

  if (!preview) return null
  return (
    <div
      role="status"
      className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-accent/30 bg-accent-soft px-3 py-2"
    >
      <Eye className="size-4 shrink-0 text-accent" />
      <p className="min-w-0 flex-1 text-[13px]">
        <span className="font-medium">Previewing unsaved changes</span>
        <span className="text-muted"> — {preview.plan.intent}</span>
        {error ? <span className="block text-xs text-danger">{error}</span> : null}
      </p>
      <Button variant="ghost" size="sm" onClick={stop}>
        <X />
        Discard
      </Button>
      <Button variant="primary" size="sm" loading={apply.isPending} onClick={() => apply.mutate()}>
        <Check />
        Apply
      </Button>
    </div>
  )
}
